import type { ComponentChildren } from 'preact';
import type { Citation } from './types';

/**
 * Minimal, safe markdown for chat bubbles: paragraphs, "- " lists, **bold**, [links](https://…)
 * and [n] citation markers. Output is Preact elements, never HTML strings, so message text can't
 * inject markup. Only http(s) links are rendered as links.
 */
const INLINE = /\*\*([^*]+)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\[(\d{1,2})\]/g;

function inline(text: string, citations: Citation[], key: string): ComponentChildren[] {
  const out: ComponentChildren[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${key}-${i++}`;
    if (m[1] !== undefined) {
      out.push(<strong key={k}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      out.push(
        <a key={k} href={m[3]} target="_blank" rel="noopener noreferrer">
          {m[2]}
        </a>,
      );
    } else {
      const n = Number(m[4]);
      const c = citations.find((x) => x.n === n);
      out.push(
        c?.url ? (
          <sup key={k}>
            <a href={c.url} target="_blank" rel="noopener noreferrer" title={c.title ?? c.url}>
              {n}
            </a>
          </sup>
        ) : (
          <sup key={k}>{n}</sup>
        ),
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function renderText(body: string, citations: Citation[] = []): ComponentChildren[] {
  const blocks = body.replace(/\r\n?/g, '\n').split(/\n{2,}/);
  return blocks.map((block, b) => {
    const lines = block.split('\n');
    if (lines.every((l) => /^\s*[-*•]\s+/.test(l))) {
      return (
        <ul key={b}>
          {lines.map((l, i) => (
            <li key={i}>{inline(l.replace(/^\s*[-*•]\s+/, ''), citations, `${b}-${i}`)}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={b}>
        {lines.flatMap((l, i) =>
          i === 0
            ? inline(l, citations, `${b}-${i}`)
            : [<br key={`br-${i}`} />, ...inline(l, citations, `${b}-${i}`)],
        )}
      </p>
    );
  });
}
